import PropTypes from 'prop-types';
import React, {
    createContext,
    useContext
} from 'react';

import { useInfiniteScroll } from '$hooks/ajax-listing';

const AjaxListingContext = createContext({});
AjaxListingContext.displayName = 'AjaxListingContext';

/* ========================================================================= */
/* Components */
/* ========================================================================= */

/**
 * @component NextButton
 * @props (see propTypes)
 */
const AjaxListingNextButton = (props) => {
    const { page, setPage, totalPages } = useContext(AjaxListingContext);

    return (
        <button
            aria-label='Next page'
            className={props.className}
            disabled={totalPages <= page}
            onClick={setPage.bind(this, page + 1)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @component PreviousButton
 * @props (see propTypes)
 */
const AjaxListingPreviousButton = (props) => {
    const { page, setPage } = useContext(AjaxListingContext);

    return (
        <button
            aria-label='Previous page'
            className={props.className}
            disabled={page <= 1}
            onClick={setPage.bind(this, page - 1)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @component PageButton
 * @props (see propTypes)
 */
const AjaxListingPageButton = (props) => {
    const { page, setPage } = useContext(AjaxListingContext);

    return (
        <button
            aria-label={`page ${props.page}`}
            className={props.className}
            disabled={page === props.page}
            onClick={setPage.bind(this, props.page)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @component InfinitePagination
 * @props (see propTypes)
 */
const InfinitePagination = (props) => {
    const {
        loading,
        page,
        setPage,
        totalPages
    } = useContext(AjaxListingContext);

    const hasNextPage = totalPages > page;

    const infiniteRef = useInfiniteScroll({
        loading: loading,
        hasNextPage: hasNextPage,
        onLoad: () => {
            setPage(page + 1);
        }
    });

    if (hasNextPage || loading) {
        return (
            <div
                aria-label='Load more'
                className={props.className}
                ref={infiniteRef}
                tabIndex='0'>
                {props.children}
            </div>
        );
    };

    return null;
};

/* ========================================================================= */
/* Typing */
/* ========================================================================= */

AjaxListingNextButton.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    className: PropTypes.string
};

AjaxListingPreviousButton.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    className: PropTypes.string
};

AjaxListingPageButton.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    className: PropTypes.string,
    page: PropTypes.number.isRequired
};

InfinitePagination.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    className: PropTypes.string
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export {
    AjaxListingContext,
    AjaxListingNextButton,
    AjaxListingPageButton,
    AjaxListingPreviousButton,
    InfinitePagination
};
